import { useMutation, useQueryClient } from '@tanstack/react-query';

import type {
  ManualPaymentMethod,
  PatchAdminV1StoresStoreIdPaymentMethodsIdBody,
  PostAdminV1StoresStoreIdPaymentMethodsBody,
} from '@nucleus-commerce/api-client/admin';

import { paymentMethodsApi } from '../api/payment-methods-api';

import { paymentMethodKeys } from './payment-method-keys';

export function useCreatePaymentMethod(storeId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body: PostAdminV1StoresStoreIdPaymentMethodsBody) =>
      paymentMethodsApi.create(storeId, body),
    meta: { successMessage: 'paymentMethods:toasts.created', silentError: true },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: paymentMethodKeys.all(storeId) }),
  });
}

/**
 * The active toggle is the one optimistic update: flipping a boolean is idempotent and the row
 * would otherwise lag behind the switch. Everything else waits for the server.
 */
export function useUpdatePaymentMethod(storeId: string) {
  const queryClient = useQueryClient();
  const key = paymentMethodKeys.list(storeId);
  return useMutation({
    mutationFn: ({
      id,
      body,
    }: {
      id: string;
      body: PatchAdminV1StoresStoreIdPaymentMethodsIdBody;
    }) => paymentMethodsApi.update(storeId, id, body),
    meta: { successMessage: 'paymentMethods:toasts.updated', silentError: true },
    onMutate: async ({ id, body }) => {
      if (body.isActive === undefined || Object.keys(body).length !== 1) return undefined;
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<ManualPaymentMethod[]>(key);
      queryClient.setQueryData<ManualPaymentMethod[]>(key, (rows) =>
        rows?.map((row) =>
          row.id === id ? { ...row, isActive: body.isActive ?? row.isActive } : row,
        ),
      );
      return { previous };
    },
    onError: (_error, _vars, context) => {
      if (context?.previous) queryClient.setQueryData(key, context.previous);
    },
    onSettled: () => queryClient.invalidateQueries({ queryKey: paymentMethodKeys.all(storeId) }),
  });
}

/** 409 RESOURCE_IN_USE while unpaid orders reference the method; the dialog explains. */
export function useDeletePaymentMethod(storeId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => paymentMethodsApi.remove(storeId, id),
    meta: { successMessage: 'paymentMethods:toasts.deleted', silentError: true },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: paymentMethodKeys.all(storeId) }),
  });
}
